import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MethodsHelper } from '@app/helpers/methods.helper';
import { Branch } from '@app/models/branch';
import { Loan } from '@app/models/loan';
import { Constants } from "@app/constants/constants";
import { GroupService } from "@app/services/group.service";
import { TranslateService } from '@ngx-translate/core';
import { CollateralsService } from '@app/services/collaterals.service';
import { GuaranteeService } from '@app/services/guarantee.service';
import { LoanService } from '@app/services/loan.service';
import { NotificationService } from '@app/services/notification.service';
import { GlobalErrorHandlerService } from '@app/services/global-error-handler.service';
import { Collateral } from '@app/models/collateral';
import { GuaranteeAgreement } from '@app/models/guarantee-agreement';

@Component({
    selector: 'app-loan-detail-summary',
    templateUrl: './loan-detail-summary.component.html',
    styleUrls: ['./loan-detail-summary.component.css'],
    standalone: false
})
export class LoanDetailSummaryComponent implements OnInit, OnChanges {

  @Input() loan: Loan;
  @Input() isLoanApproval: boolean;
  @Input() customerGroupId: string;
  @Input() branches: Array<Branch>;
  @Input() showCustomerLink: boolean = true;
  @Output() onClose = new EventEmitter<void>();

  notesHierarchy: any[] = [];
  collaterals: Collateral[] = [];
  guarantees: GuaranteeAgreement[] = [];
  interestCalculationOptions = Constants.INTEREST_CALCULATION_OPTIONS;

  loanFiles: any[] = [];
  displayBasic = false;
  activeIndex = 0;
  errorMessage: string | boolean;

  upfrontChargeOperation(type: string): any {
    return this.loan?.ops?.find(op => !op?.deleted && (op?.type === type || op?.operationType === 'LOAN_' + type + '_CHARGING' || op?.['@type'] === type));
  }

  constructor(
    private customerGroupService: GroupService,
    private loanService: LoanService,
    private collateralService: CollateralsService,
    private guaranteeService: GuaranteeService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private errorService: GlobalErrorHandlerService
  ) { }

  ngOnInit(): void {
    if (!this.loan) {
      this.loan = new Loan();
    } else {
      if (!this.customerGroupId) {
        this.getCustomerGroupInfo(this.loan.customerGroupId);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.loan) {
      this.loadFiles();
      this.buildNotesHierarchy(this.loan?.notes);
    }
    if (this.loan?.agreementNumber) {
      this.getCollaterals(this.loan.agreementNumber);
      this.getGuarantees(this.loan.agreementNumber);
    } else {
      this.collaterals = [];
      this.guarantees = [];
    }
  }

  getCollaterals(loanAgreementNumber?: string) {
    if (!loanAgreementNumber) {
      this.collaterals = [];
      return;
    }
    const params = {
      loanAgreementNumber: loanAgreementNumber,
      page: 0,
      size: Constants.PAGE_UNLIMITED_ITEM_COUNT
    };
    this.collateralService.getList(params).subscribe({
      next: (data) => {
        if (data && data.content) {
          this.collaterals = data.content;
        } else if (Array.isArray(data)) {
          this.collaterals = data;
        } else {
          this.collaterals = [];
        }
      },
      error: () => this.collaterals = []      
    });
  }

  getGuarantees(loanAgreementNumber?: string) {
    if (!loanAgreementNumber) {
      this.guarantees = [];
      return;
    }
    const params = {
      loanAgreementNumber: loanAgreementNumber,
      page: 0,
      size: Constants.PAGE_UNLIMITED_ITEM_COUNT
    };
    this.guaranteeService.getList(params).subscribe({
      next: (data) => {
        if (data && data.content) {
          this.guarantees = data.content;
        } else if (Array.isArray(data)) {
          this.guarantees = data;
        } else {
          this.guarantees = [];
        }
      },
      error: () => this.guarantees = []      
    });
  }

  loadFiles() {
    this.loanFiles = [];
    if (this.loan?.files?.length && this.loan?.id) {
      this.loan.files.forEach((file: any) => {
        this.getFiles(file);
      });
    }
  }

  getFiles(file: any) {
    if (!file?.id || !this.loan?.id) return;
    this.loanService.getFileById(file.id, this.loan.id).subscribe({
      next: (data: any) => {
        let reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
          let base64data = reader.result as string;
          const newFile = {
            id: file.id,
            title: file.title || file.name,
            data: data,
            itemImageSrc: base64data.replace('data:application/octet-stream;', 'data:image/png;')
          };
          const existingIndex = this.loanFiles.findIndex(f => f.id === file.id);
          if (existingIndex > -1) {
            this.loanFiles[existingIndex] = newFile;
          } else {
            this.loanFiles.push(newFile);
          }
        };
      },
      error: (error) => this.notificationService.showErrorMessage(this.errorService.handleError(error))
    });
  }

  formatNumber(value) {
    if (value === null || value === undefined || isNaN(value)) return '';
    return MethodsHelper.numberWithCommas(value);
  }

  getFormatDate(yyyymmdd) {
    if (yyyymmdd) {
      let items = yyyymmdd.split("-");
      if (items.length == 3) {
        return items[2] + "." + items[1] + "." + items[0].substr(2);
      }
    }
    return "";
  }

  findSymbol() {
    if (this.loan && this.loan.currency) {
      return MethodsHelper.findSymbol(this.loan.currency);
    }
    return "";
  }

  formatGracePeriod(value) {
    if (value) {
      let graceNumber = value.substring(1, value.length - 1);
      if (value[length - 1] == 'D') {
        return graceNumber + " days";
      } else if (value[length - 1] == 'Y') {
        return graceNumber + " years";
      }
      return graceNumber + " months";
    }
    return "";
  }

    findInterestCalculationOptions(type: string): string {
        const itemType = this.interestCalculationOptions.find(p => p.value === type);
        if (itemType) {
            return this.translate.instant(itemType.label);
        }
        return '';
    }


    loanPeriodGenType(type) {
    let typePeriod = '';
    switch (type) {
      case "M": typePeriod = "months";
        break;
      case "Y": typePeriod = "years";
        break;
      case "W": typePeriod = "weeks";
        break;
      case "D": typePeriod = 'days';
        break;
      default: typePeriod = 'unknown'
    }
    return typePeriod;
  }

  getLoanPeriodGen() {
    if (this.loan && this.loan.period) {
      let typePeriod = this.loanPeriodGenType(this.loan.period[this.loan.period.length - 1]);
      return this.loan.period.substring(1, this.loan.period.length - 2) + " " + typePeriod;
    }
    return "";
  }

  loanPeriodCovertToString(){
    let period = '';
    if (this.loan && this.loan.period) {
      let lastChar = this.loan.period.charAt(this.loan.period.length-1);
      period = this.loan.period.substring(1);
      let lastPart = '';
      switch (lastChar){
        case "M":
          lastPart = "Month(s)";
          break;
        case "Y":
          lastPart = "Year(s)";
          break;
        case "D":
          lastPart = "Day(s)";
          break;
        case "W":
          lastPart = "Week(s)";
          break;
        default:
          break;
      }
      period = period.slice(0,-1) +" " +lastPart;
    }
    return period;
  }

  getBranchName(branchId) {
    if (this.branches) {
      let item = this.branches.find(p => p.id == branchId);
      if (item) {
        return item.name;
      }
    }
    return "";
  }

  getBranchNameFromCode(branchCode) {
    if (this.branches) {
      let item = this.branches.find(p => p.code == branchCode);
      if (item) {
        return item.name;
      }
    }
    return "";
  }

  getUpfrontFee(){
    return this.getResponseFeeAmount(this.loan?.upfrontFee, this.loan?.upfrontFeeAmount);
  }

  getUpfrontServiceFee() {
    return this.getResponseFeeAmount(this.loan?.serviceUpfrontFee, this.loan?.serviceUpfrontFeeAmount);
  }

  private getResponseFeeAmount(enabled: boolean, amount: number) {
    if (!this.loan) {
      return '';
    }

    if (!enabled) {
      return 'No';
    }

    if (MethodsHelper.haveValue(amount)) {
      return MethodsHelper.formatNumberWithCommas(amount) + " " + this.loan.currency;
    }

    return '';
  }

  getLoanServiceFeeAmount(){
    return MethodsHelper.getLoanServiceFeeAmount(this.loan);
  }

  getLatePaymentFeePrincipal() {
    return MethodsHelper.getLatePaymentFeePrincipal(this.loan);
  }

  getLatePaymentFeeInterest() {
    return MethodsHelper.getLatePaymentFeeInterest(this.loan);
  }

  getLatePaymentFeeServicefee() {
    return MethodsHelper.getLatePaymentFeeServicefee(this.loan);
  }

  getCustomerGroupInfo(customerGroupId: string | undefined) {
    if(customerGroupId){
      this.customerGroupService.getById(customerGroupId).subscribe(data => {
        if(data){
          this.customerGroupId = data.groupId
        }
      });
    }
  }

  buildNotesHierarchy(data: any[]) {
    if (!data) return;

    const hierarchyMap = new Map<string, { parent: string, children: string[] }>();

    data.forEach(item => {
        if (item.parentId) {
            const parent = data.find(parentItem => parentItem.id === item.parentId);
            if (parent) {
                if (!hierarchyMap.has(parent.id)) {
                    hierarchyMap.set(parent.id, { parent: parent.name, children: [] });
                }
                hierarchyMap.get(parent.id)?.children.push(item.name);
            }
        } else {
            if (!hierarchyMap.has(item.id)) {
                hierarchyMap.set(item.id, { parent: item.name, children: [] });
            }
        }
    });

    this.notesHierarchy = Array.from(hierarchyMap.values()).map(({ parent, children }) => ({
        parent,
        child: children.length ? children.join(', ') : null
    }));
  }

  showAttachedFiles() {
    if (!this.loan?.files || this.loan.files.length === 0) return '';
    const allTitles = this.loan.files.map((f: any) => f.title || f.name).filter(t => t && t.trim() !== "");
    return Array.from(new Set(allTitles)).join(', ');
  }

  downloadFile(item: any) {
    if (item?.data) {
      const blob = item.data instanceof Blob ? item.data : new Blob([item.data], { type: 'application/docx' });
      const objectUrl = URL.createObjectURL(blob);
      let link = document.createElement('a');
      link.href = objectUrl;
      link.download = item.title || item.name || 'download';
      link.click();
      window.URL.revokeObjectURL(link.href);
    } else if (item?.id && this.loan?.id) {
      this.loanService.getFileById(item.id, this.loan.id).subscribe((res: any) => {
        if (res) {
          const blob = res instanceof Blob ? res : new Blob([res], { type: res.type || 'application/docx' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = item.title || item.name || 'download';
          link.click();
          window.URL.revokeObjectURL(url);
        }
      });
    }
  }

  openFile(index: number = 0) {
    this.activeIndex = index;
    this.displayBasic = true;
  }

  formatUppercaseText(str: string | null | undefined) {
    return MethodsHelper.textCapitalize(str);
  }

  formatTextCapitalize(str: string) {
    if (!str) return '';
    if (str.indexOf('_') !== -1) {
      str = str.split('_').join(' ');
    }
    return MethodsHelper.textCapitalize(str);
  }

  isProviderSameAsLoanCustomer(item: Collateral): boolean {
    if (!item || !this.loan) {
      return false;
    }
    const loanCustomerIds = [
      this.loan.customerId,
      ...(this.loan.customerSpecificOptions?.map(c => c.customerId) || [])
    ].filter(Boolean);

    const loanCustomerNumbers = [
      ...(this.loan.customerSpecificOptions?.map(c => c.customerNumber) || [])
    ].filter(Boolean);

    if (item.providerId && loanCustomerIds.includes(item.providerId)) {
      return true;
    }
    if (item.providerNumber && loanCustomerNumbers.includes(item.providerNumber)) {
      return true;
    }
    return false;
  }

  getProviderCustomerUrl(item: Collateral): string | null {
    if (!item?.providerId || this.isProviderSameAsLoanCustomer(item)) {
      return null;
    }
    return '/user/customers/view/' + item.providerId;
  }

  onProviderClick(item: Collateral) {
    if (this.isProviderSameAsLoanCustomer(item)) {
      this.onClose.emit();
    }
  }

  isGuarantorSameAsLoanCustomer(item: GuaranteeAgreement): boolean {
    if (!item || !this.loan) {
      return false;
    }
    const loanCustomerIds = [
      this.loan.customerId,
      ...(this.loan.customerSpecificOptions?.map(c => c.customerId) || [])
    ].filter(Boolean);

    const loanCustomerNumbers = [
      ...(this.loan.customerSpecificOptions?.map(c => c.customerNumber) || [])
    ].filter(Boolean);

    if (item.guarantorId && loanCustomerIds.includes(item.guarantorId)) {
      return true;
    }
    if (item.guarantorNumber && loanCustomerNumbers.includes(item.guarantorNumber)) {
      return true;
    }
    return false;
  }

  getGuarantorCustomerUrl(item: GuaranteeAgreement): string | null {
    if (!item?.guarantorId || this.isGuarantorSameAsLoanCustomer(item)) {
      return null;
    }
    return '/user/customers/view/' + item.guarantorId;
  }

  onGuarantorClick(item: GuaranteeAgreement) {
    if (this.isGuarantorSameAsLoanCustomer(item)) {
      this.onClose.emit();
    }
  }

  getUpfrontVAT(){
    return this.getResponseFeeAmount(this.loan?.upfrontVAT, this.loan?.upfrontVATAmount);
  }

  getUpfrontStampDuty(){
    return this.getResponseFeeAmount(this.loan?.upfrontStampDuty, this.loan?.upfrontStampDutyAmount);
  }

  getUpfrontDisasterRecovery(){
    return this.getResponseFeeAmount(this.loan?.upfrontDisasterRecovery, this.loan?.upfrontDisasterRecoveryAmount);
  }
}
